import React, { useMemo, useState } from 'react';
import commonStyles from '../../styles/automation/index.module.scss';
import styles from '../../styles/automation/executionmanager.module.scss';
import ExecutionResultsPanel from './Results/ExecutionResultsPanel';
import { getExecutionFilesByIds } from '../../utils/api/executionFileApis';
import { STATUS } from '../../utils/api/sharedfunction';
import { getReplayUserFacingErrorMessage } from '../AutomationStudio/utils/stepExecutionDisplay';

const TITLE = 'Execution Steps';

export default function ExecutionResultSteps({
    executionSteps = [],
    mcpToolsList = [],
    mcpToolMap = {},
    isAiDynamicRun = false,
    emptyPanelMessage = null,
}) {
    const totalSteps = useMemo(() => executionSteps.length, [executionSteps]);
    const [selectedStep, setSelectedStep] = useState(null);
    const [executionFilesLoading, setExecutionFilesLoading] = useState(false);

    // Calculate test summary from execution steps
    const testSummary = useMemo(() => {
        if (!executionSteps || executionSteps.length === 0) {
            return { totalSteps: 0, passed: 0, failed: 0 };
        }
        
        const passed = executionSteps.filter(step => step.passed === true).length;
        const failed = executionSteps.filter(step => step.passed === false).length;
        
        return {
            totalSteps: executionSteps.length,
            passed: passed,
            failed: failed
        };
    }, [executionSteps]);


    const handleSelectStep = async (step) => {
        const mappedStep = {
            id: step.id,
            stepNumber: executionSteps.indexOf(step) + 1,
            stepDescription: step.stepDescription || step.description || 'Step Description',
            passed: step.passed,
            duration: step.duration,
            actualOutcome: step.actualOutcome,
            expectedOutcome: step.expectedOutcome,
            reason: step.reason,
            confidence: step.confidence ?? step.args?.confidence,
            screenshotPath: step.screenshotPath,
            timestamp: step.timestamp,
            playwrightAssertion: step.playwrightAssertion,
            isFinal: step.isFinal,
            executionStrategies: step.executionStrategies || [],
            executedStrategy: step.executedStrategy,
            executionId: step.executionId,
            isCustomStep: step.isCustomStep,
            logs: step.logs || [],
            error: step?.error || getReplayUserFacingErrorMessage(step) || null,
            isPrimaryAIEvaluation: step.isPrimaryAIEvaluation,
            llmFallback: step.llmFallback,
            llmConfidence: step.llmConfidence ?? step.args?.llmConfidence,
            llmReason: step.llmReason,
            llmDuration: step.llmDuration,
            llmFallbackDisabled: step.llmFallbackDisabled,
            llmFallbackAttempted: step.llmFallbackAttempted,
            llmFallbackFailed: step.llmFallbackFailed,
            testSummary: testSummary,
            tool: step.tool,
            args: step.args || {},
            executionFileIds: step.executionFileIds || [],
            // Dynamic / flattened Lambda payload
            code: step.code,
            toolCallTrace: step.toolCallTrace,
            toolCallSteps: step.toolCallSteps,
            automationPhase: step.automationPhase,
            instructionId: step.instructionId,
            locator: step.locator,
            selfHealed: step.selfHealed,
            healTool: step.healTool,
            healArgs: step.healArgs,
            healAttempt: step.healAttempt,
            healMethod: step.healMethod,
            healedRef: step.healedRef,
            dynamicInstructionIndex: step.dynamicInstructionIndex,
            isDynamic: step.isDynamic,
            dynamicIntent: step.dynamicIntent,
            stepWiseDynamicResults: step.stepWiseDynamicResults,
            runTimeExpectedOutcome: step.runTimeExpectedOutcome,
        };

        if (step.executionFileIds?.length) {
            setExecutionFilesLoading(true);
            try {
                const res = await getExecutionFilesByIds({ fileIds: step.executionFileIds });
                mappedStep.executionFiles = (res.status === STATUS.SUCCESS && res.data?.files) ? res.data.files : [];
            } catch {
                mappedStep.executionFiles = [];
            } finally {
                setExecutionFilesLoading(false);
            }
        } else {
            mappedStep.executionFiles = [];
        }

        setSelectedStep(mappedStep);
    };


    return (
        <div className={`${commonStyles.containerWrapper} ${styles.containerLayout}`}>
            <div className={styles.boxContainer}>
                <div className={styles.box1}>
                    <div className={styles.executionStepsTitleRow}>
                        <span role="title" aria-level={TITLE} aria-label={TITLE} className={commonStyles.title}>{TITLE}</span>
                        {isAiDynamicRun && (
                            <span className={styles.aiModeTag} title="Executed via AI mode">AI mode</span>
                        )}
                    </div>
                    <div className={`${styles.stepBoxContainerWrapper} ${styles.stepsScrollableContent}`}>
                        {
                            executionSteps.map((step, index) => {
                                let stepNumber = index + 1;
                                return (
                                    <div key={step.id || stepNumber} className={`${styles.stepBoxContainer} ${selectedStep?.id === step.id ? styles.active : ''}`} 
                                        onClick={() => handleSelectStep(step)} role="button" tabIndex={0} aria-level={stepNumber} aria-label={step.stepDescription}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSelectStep(step)}
                                    >
                                        <div className={styles.stepBox}>
                                            <div className={styles.leftSide}>
                                                <span className={styles.stepNumber}>{stepNumber}</span>
                                                <div className={styles.stepDetails}>
                                                    <span className={styles.stepTitle}>
                                                        {step.stepDescription || step.description || `Step ${stepNumber}`}
                                                    </span>
                                                </div>
                                            </div>
                                            <span className={styles.rightSide}>
                                                <div className={styles.rightSideIcons}>
                                                    {step.isCustomStep && <span className={styles.tag}>custom</span>}
                                                    {step.isPrimaryAIEvaluation && <span className={`${styles.tag} ${styles.aiHealedTag}`}>AI</span>}
                                                    
                                                    <span className={`${styles.statusBadge} ${step.passed ? styles.success : styles.failed}`}>
                                                        {step.passed ? '✓' : '✗'}
                                                    </span>
                                                </div>
                                            </span>
                                        </div>
                                    </div>
                                )
                            })
                        }
                    </div>
                </div>
                <div className={styles.box2}>
                    <ExecutionResultsPanel 
                        selectedStep={selectedStep} 
                        testSummary={testSummary}
                        mcpToolsList={mcpToolsList}
                        mcpToolMap={mcpToolMap}
                        executionFilesLoading={executionFilesLoading}
                        emptyStateMessage={emptyPanelMessage}
                    />
                </div>
            </div>
        </div>
    );
}
